import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useCart } from '@/App';
import { toast } from 'sonner';
import { ShoppingCart, Shield, Star, Gift, ChevronRight, CheckCircle, Clock, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

export default function MysteryBoxPage() {
  const [mysteryBoxes, setMysteryBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart, loading: cartLoading } = useCart();

  useEffect(() => {
    const fetchMysteryBoxes = async () => {
      try {
        const response = await axios.get(`${API}/mystery-boxes`);
        setMysteryBoxes(response.data);
      } catch (error) {
        console.error('Error fetching mystery boxes:', error);
        // Fallback to regular products endpoint
        const fallback = await axios.get(`${API}/products?category=mystery_box`);
        setMysteryBoxes(fallback.data);
      } finally {
        setLoading(false);
      }
    };

    fetchMysteryBoxes();
  }, []);

  const handleAddToCart = async (product) => {
    if (product.coming_soon || !product.in_stock) {
      toast.error('This product is not available yet');
      return;
    }
    try {
      await addToCart(product.product_id);
      toast.success(`${product.name} added to cart!`);
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  const availableBoxes = mysteryBoxes.filter(box => !box.coming_soon);
  const comingSoonBoxes = mysteryBoxes.filter(box => box.coming_soon);

  return (
    <div className="pt-20 min-h-screen" data-testid="mystery-box-page">
      {/* Hero Section */}
      <section className="relative py-20 hero-glow border-b border-[#27272A]">
        <div className="container-main">
          <div className="max-w-3xl">
            <span className="label-caps text-[#C41E3A] mb-4 block">Limited Edition Releases</span>
            <h1 className="heading-display text-4xl md:text-6xl text-white mb-6" data-testid="mystery-box-title">
              Mystery Boxes
            </h1>
            <p className="text-lg md:text-xl text-white/70 mb-8">
              Chase legendary hits! Each box is hand-numbered and contains authenticated autographed memorabilia. 
              Will you pull the GOAT?
            </p>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#C41E3A]" />
                <span className="text-white/70 text-sm">100% Authenticated</span>
              </div>
              <div className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-[#C41E3A]" />
                <span className="text-white/70 text-sm">Hand Numbered</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-[#C41E3A]" />
                <span className="text-white/70 text-sm">Premium Hits Only</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Available Mystery Boxes */}
      <section className="section-gap" data-testid="available-boxes">
        <div className="container-main">
          <h2 className="heading-display text-3xl text-white mb-10">Available Now</h2>

          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {[1, 2].map(i => (
                <div key={i} className="bg-[#0A0A0A] border border-[#27272A] h-[600px] animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-12">
              {availableBoxes.map((box) => (
                <MysteryBoxCard key={box.product_id} box={box} onAddToCart={handleAddToCart} cartLoading={cartLoading} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Coming Soon */}
      {comingSoonBoxes.length > 0 && (
        <section className="section-gap bg-[#0A0A0A]" data-testid="coming-soon-boxes">
          <div className="container-main">
            <h2 className="heading-display text-3xl text-white mb-10">
              <Clock className="inline-block w-8 h-8 mr-3 text-[#C41E3A]" />
              Coming Soon
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {comingSoonBoxes.map((box) => (
                <div key={box.product_id} className="bg-[#111111] border border-[#27272A] p-8 opacity-75">
                  <Badge className="bg-[#27272A] text-white/70 font-barlow uppercase text-xs px-3 py-1 mb-4">
                    {box.series_name}
                  </Badge>
                  <h3 className="font-barlow font-bold text-xl text-white mb-4">{box.name}</h3>
                  <p className="text-white/50 mb-6">{box.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="font-barlow font-black text-2xl text-white/50">${box.price.toFixed(2)}</span>
                    <Badge className="bg-[#C41E3A]/20 text-[#C41E3A] font-barlow uppercase">Coming Soon</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Hits Gallery CTA */}
      <section className="py-16 border-t border-[#27272A]">
        <div className="container-main text-center">
          <h2 className="heading-display text-2xl text-white mb-4">See What Others Have Pulled!</h2>
          <p className="text-white/60 mb-8">Check out our Hits Gallery for real unboxing videos</p>
          <Link to="/hits-gallery">
            <Button className="btn-primary flex items-center gap-2 mx-auto">
              View Hits Gallery <ChevronRight className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}

function MysteryBoxCard({ box, onAddToCart, cartLoading }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-[#0A0A0A] border border-[#27272A] overflow-hidden" data-testid={`mystery-box-${box.product_id}`}>
      <div className="grid grid-cols-1 lg:grid-cols-2">
        {/* Image Section */}
        <div className="relative aspect-square lg:aspect-auto bg-[#111111]">
          <img
            src={box.images?.[0] || 'https://images.unsplash.com/photo-1571461542309-dedaaec54069?w=800'}
            alt={box.name}
            className="w-full h-full object-cover"
          />
          {/* Edition Badge */}
          <div className="absolute top-4 left-4">
            <Badge className="bg-[#C41E3A] text-white font-barlow uppercase text-sm px-4 py-2">
              {box.series_name} • {box.editions_remaining}/{box.edition_total} Remaining
            </Badge>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-8 lg:p-10">
          {/* Title & Price */}
          <h3 className="font-barlow font-black text-2xl lg:text-3xl text-white mb-4 uppercase">
            {box.name}
          </h3>
          
          <div className="flex items-center gap-4 mb-6">
            <span className="font-barlow font-black text-4xl text-[#C41E3A]">
              ${box.price.toFixed(2)}
            </span>
            <Badge className="bg-green-600/20 text-green-500 font-barlow uppercase text-xs px-3 py-1">
              In Stock
            </Badge>
          </div>

          {/* Description */}
          <p className="text-white/70 mb-6 leading-relaxed">
            {box.description}
          </p>

          {/* Features */}
          {box.box_features && (
            <div className="mb-6">
              <h4 className="font-barlow font-bold text-white mb-3 uppercase text-sm tracking-wider">Features</h4>
              <ul className="space-y-2">
                {box.box_features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2 text-white/70">
                    <CheckCircle className="w-4 h-4 text-[#C41E3A] mt-0.5 shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Headline Hits */}
          {box.headline_hits && box.headline_hits.length > 0 && (
            <div className="mb-6">
              <h4 className="font-barlow font-bold text-white mb-3 uppercase text-sm tracking-wider">
                <Star className="inline-block w-4 h-4 mr-2 text-yellow-500" />
                Chase These Hits
              </h4>
              <div className="flex flex-wrap gap-2">
                {box.headline_hits.map((hit, index) => (
                  <Badge key={index} className="bg-yellow-500/20 text-yellow-500 font-barlow text-sm px-3 py-1">
                    {hit}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Authentication */}
          {box.authentication_partners && (
            <div className="mb-6">
              <h4 className="font-barlow font-bold text-white mb-3 uppercase text-sm tracking-wider">
                <Shield className="inline-block w-4 h-4 mr-2 text-[#C41E3A]" />
                Authentication Partners
              </h4>
              <div className="flex flex-wrap gap-2">
                {box.authentication_partners.map((partner, index) => (
                  <Badge key={index} className="bg-[#27272A] text-white/70 font-barlow text-xs px-3 py-1">
                    {partner}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Possible Hits (Expandable) */}
          {box.possible_hits && box.possible_hits.length > 0 && (
            <div className="mb-8">
              <button
                onClick={() => setExpanded(!expanded)}
                className="font-barlow font-bold text-white uppercase text-sm tracking-wider flex items-center gap-2 hover:text-[#C41E3A] transition-colors"
                data-testid="expand-hits-btn"
              >
                View All Possible Hits ({box.possible_hits.length})
                <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
              </button>
              
              {expanded && (
                <div className="mt-4 p-4 bg-[#111111] border border-[#27272A] max-h-60 overflow-y-auto">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {box.possible_hits.map((hit, index) => (
                      <span key={index} className="text-white/60 text-sm">{hit}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Add to Cart */}
          <Button
            onClick={() => onAddToCart(box)}
            disabled={cartLoading || !box.in_stock}
            className="btn-primary w-full flex items-center justify-center gap-2"
            data-testid={`add-to-cart-${box.product_id}`}
          >
            <ShoppingCart className="w-5 h-5" />
            Add to Cart
          </Button>
        </div>
      </div>
    </div>
  );
}
